import Object from 'tweetnacl'; // Dummy replacement to keep imports clean for diff
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { argon2id } from 'hash-wasm';
import { clearDB } from "./db";

let currentMasterKey = null;

const bc = new BroadcastChannel("stream_keys");

bc.onmessage = (e) => {
  if (e.data.type === "REQUEST_KEY" && currentMasterKey) {
    bc.postMessage({ type: "SEND_KEY", key: naclUtil.encodeBase64(currentMasterKey) });
  } else if (e.data.type === "SEND_KEY" && !currentMasterKey) {
    currentMasterKey = naclUtil.decodeBase64(e.data.key);
    sessionStorage.setItem("stream_mk", e.data.key);
  }
};

export function setMasterKey(mk) {
  currentMasterKey = mk;
  if (mk) {
    sessionStorage.setItem("stream_mk", naclUtil.encodeBase64(mk));
  } else {
    sessionStorage.removeItem("stream_mk");
  }
}

export function getMasterKey() {
  if (currentMasterKey) return currentMasterKey;
  
  const stored = sessionStorage.getItem("stream_mk");
  if (stored) {
    currentMasterKey = naclUtil.decodeBase64(stored);
    return currentMasterKey;
  }
  
  // If not in session, ask other tabs
  bc.postMessage({ type: "REQUEST_KEY" });
  return null;
}

export function clearMasterKey() {
  currentMasterKey = null;
  sessionStorage.removeItem("stream_mk");
  localStorage.removeItem("stream_mk"); // Cleanup old fallback
  clearDB().catch(console.error); // Wipe decrypted local DB
}

// 1. Derive KEK (Key Encryption Key) from password and email (salt) using Argon2id
export async function deriveKEK(password, email) {
  const salt = "stream_app_salt_" + email.toLowerCase().trim();
  const key = await argon2id({
    password: password,
    salt: salt,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536, // 64 MB
    hashLength: 32, // 256-bit for AES/Salsa
    outputType: 'binary'
  });
  return key;
}

// 2. Generate a new random Master Key (MK)
export function generateMasterKey() {
  return nacl.randomBytes(nacl.secretbox.keyLength);
}

// 3. Encrypt the Master Key with the KEK
export function encryptMasterKey(mk, kek) {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const box = nacl.secretbox(mk, nonce, kek);
  const fullMessage = new Uint8Array(nonce.length + box.length);
  fullMessage.set(nonce);
  fullMessage.set(box, nonce.length);
  return naclUtil.encodeBase64(fullMessage);
}

// 4. Decrypt the Master Key with the KEK
export function decryptMasterKey(emkBase64, kek) {
  const messageWithNonce = naclUtil.decodeBase64(emkBase64);
  const nonce = messageWithNonce.slice(0, nacl.secretbox.nonceLength);
  const message = messageWithNonce.slice(nacl.secretbox.nonceLength);
  const decrypted = nacl.secretbox.open(message, nonce, kek);
  if (!decrypted) throw new Error("Failed to decrypt master key. Invalid password or corrupted key.");
  return decrypted;
}

// 5. Encrypt string data with Master Key
export function encryptData(text, mk) {
  if (!text) return text;
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const messageUint8 = naclUtil.decodeUTF8(text);
  const box = nacl.secretbox(messageUint8, nonce, mk);
  const fullMessage = new Uint8Array(nonce.length + box.length);
  fullMessage.set(nonce);
  fullMessage.set(box, nonce.length);
  return naclUtil.encodeBase64(fullMessage);
}

// 6. Decrypt string data with Master Key
export function decryptData(ciphertextBase64, mk) {
  if (!ciphertextBase64) return ciphertextBase64;
  try {
    const messageWithNonce = naclUtil.decodeBase64(ciphertextBase64);
    const nonce = messageWithNonce.slice(0, nacl.secretbox.nonceLength);
    const message = messageWithNonce.slice(nacl.secretbox.nonceLength);
    const decrypted = nacl.secretbox.open(message, nonce, mk);
    if (!decrypted) return "[Decryption Failed]";
    return naclUtil.encodeUTF8(decrypted);
  } catch (e) {
    return "[Decryption Failed - Invalid Format]";
  }
}
